import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import {
  Calendar2Date,
  Cash,
  Check,
  EyeFill,
  EyeSlashFill,
  FileEarmarkTextFill,
  PersonCircle,
  Search,
} from 'react-bootstrap-icons'
import { toast } from 'react-toastify'
import { CustomButton } from '../../../components/CustomButton'
import apinode from '../../../services/apinode'
import TrocasDevolucoesService from '../../../services/database/TrocasDevolucoesService'
import { formatDate } from '../../../utils/formatDate'
import { formatPrice } from '../../../utils/formatPrice'
import { sendErrorApiMessage } from '../../../utils/sendErrorApiMessage'

import './styles.css'

export function ModalDevolucao({ isOpen, onClose, loadRefunds }) {
  const [order, setOrder] = useState({})
  const [orderNumber, setOrderNumber] = useState('')
  const [itemsToRefund, setItemsToRefund] = useState([])
  const [isRequestLoading, setIsRequestLoading] = useState(false)
  const [isItemsVisible, setIsItemsVisible] = useState(false)
  const [isLoadingRefundRequest, setIsLoadingRefundRequest] = useState(false)
  const [willAddBalance, setWillAddBalance] = useState(false)

  const itemsVisibleButtonRef = useRef(null)
  const itemRefundRef = useRef(null)

  function handleAddItem(item) {
    setItemsToRefund((prevState) => [...prevState, item])
  }

  function handleRemoveItem(item) {
    setItemsToRefund((prevState) =>
      prevState.filter((itemD) => itemD.id !== item.id),
    )
  }

  async function handleConfirm() {
    setIsLoadingRefundRequest(true)

    try {
      const itemsFormatted = itemsToRefund.map((itemToRefund) => {
        return {
          id_produto: itemToRefund.id,
          sku_produto: itemToRefund.codigo,
          quantidade: Number(itemToRefund.quantity_to_refund),
          valorunitario:
            itemToRefund.valor_total_item / itemToRefund.quantidade_nova,
        }
      })

      await TrocasDevolucoesService.create({
        id_cliente: order.id_cliente,
        id_pedido: order.pedido,
        id_funcionario: localStorage.idusuario,
        items: itemsFormatted,
        willAddBalance,
      })

      toast.success('Devolução feita com sucesso!')
      onClose()

      loadRefunds()
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao realizar a devolução!')
    } finally {
      setIsLoadingRefundRequest(false)
    }
  }

  async function handleSearchOrder() {
    setIsRequestLoading(true)

    try {
      const response = await apinode.get(
        `/pedido/pedido-editar/${orderNumber}/${localStorage.idconta}`,
      )

      setOrder(response.data.dadospedido)
    } catch (error) {
      sendErrorApiMessage(
        error,
        `Não foi possível encontrar o pedido com o ID ${orderNumber}.`,
      )
    } finally {
      setIsRequestLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      setOrder({})
      setOrderNumber('')
      setItemsToRefund([])
      setIsRequestLoading(false)
      setIsItemsVisible(false)
    }
  }, [isOpen])

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      dialogClassName="modal-devolucoes-dialog"
      contentClassName="modal-devolucoes-content"
    >
      <Modal.Header closeButton>
        <Modal.Title>Devolução de mercadoria</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="content">
          <Form.Control
            type="text"
            placeholder="Digite o número do pedido"
            value={orderNumber}
            onChange={(e) => {
              setOrderNumber(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                itemsVisibleButtonRef.current?.click()
              }
            }}
            style={{
              width: '500px',
            }}
          />

          <CustomButton
            ref={itemsVisibleButtonRef}
            isLoading={isRequestLoading}
            isDisabled={!orderNumber}
            onClick={handleSearchOrder}
            style={{
              minHeight: 37,
            }}
          >
            <Search size={20} />
          </CustomButton>
        </div>

        {order.cliente && (
          <div className="mt-4">
            <section id="pedido">
              <strong>Dados do pedido #{order.pedido}</strong>

              <div>
                <article>
                  <PersonCircle size={20} />
                  <p>{order.cliente}</p>
                </article>

                <article>
                  <FileEarmarkTextFill size={20} />
                  <p>{order.cpf}</p>
                </article>

                <article>
                  <Calendar2Date size={20} />
                  <p>{formatDate(order.data, 'dd/MM/yyyy')}</p>
                </article>

                <article>
                  <Cash size={20} />
                  <p>{formatPrice(order.valor)}</p>
                </article>
              </div>

              <CustomButton
                title={!isItemsVisible ? 'Ver itens' : 'Ocultar itens'}
                onClick={() => setIsItemsVisible((prevState) => !prevState)}
              >
                {!isItemsVisible ? (
                  <EyeFill size={20} />
                ) : (
                  <EyeSlashFill size={20} />
                )}
              </CustomButton>
            </section>
          </div>
        )}

        {isItemsVisible && (
          <div id="items">
            {order.items.length === 0 ? (
              <p className="m-4">Não há itens nesse pedido :(</p>
            ) : (
              <>
                <p className="m-4">
                  * Digite a quantidade a ser devolvida e selecione o produto *
                </p>

                {order.items.map((item) => (
                  <Item
                    ref={itemRefundRef}
                    key={item.codigo}
                    item={item}
                    addItem={handleAddItem}
                    removeItem={handleRemoveItem}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div
          style={{
            height: 35,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            marginRight: 20,
          }}
        >
          <Form.Check
            id="check-balance"
            type="checkbox"
            disabled={itemsToRefund.length <= 0}
            checked={willAddBalance}
            onChange={() =>
              willAddBalance
                ? setWillAddBalance(false)
                : setWillAddBalance(true)
            }
          />
          Adic. Saldo
        </div>

        <CustomButton
          isDisabled={itemsToRefund.length <= 0}
          title="Confirmar"
          onClick={handleConfirm}
          isLoading={isLoadingRefundRequest}
        >
          <Check />
        </CustomButton>
      </Modal.Footer>
    </Modal>
  )
}

const Item = forwardRef(({ item, addItem, removeItem }, ref) => {
  const [quantityRefund, setQuantityRefund] = useState('')
  const [itemFromRefund, setItemFromRefund] = useState({})
  const [isChecked, setIsChecked] = useState(false)
  const [isLoadingRefund, setIsLoadingRefund] = useState(true)

  const itemPriceUnit =
    Number(item.valor_total_item) / Number(item.quantidade_nova)

  const itemPriceTotal = Number(itemPriceUnit) * item.quantidade_nova

  const checkIsDisabled =
    !quantityRefund ||
    Number(quantityRefund) <= 0 ||
    Number(quantityRefund) > Number(item.quantidade_nova) ||
    Number(quantityRefund) >
      Number(item.quantidade_nova - itemFromRefund.quantidade)

  const verifyItemRefund = useCallback(async () => {
    setIsLoadingRefund(true)

    try {
      const itemData = await TrocasDevolucoesService.getOne({
        id_produto: item.id,
        id_pedido: item.id_pedido,
      })

      if (itemData) {
        setItemFromRefund(itemData)
      }
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível recuperar as informações!')
    } finally {
      setIsLoadingRefund(false)
    }
  }, [item.id, item.id_pedido])

  useEffect(() => {
    verifyItemRefund()
  }, [verifyItemRefund])

  return (
    <>
      <div id="articles">
        {isLoadingRefund ? (
          <p>Carregando Item...</p>
        ) : (
          <>
            {Number(itemFromRefund.quantidade) ===
              Number(item.quantidade_nova) && <div id="faixa">Devolvido</div>}

            <article
              ref={ref}
              className={
                Number(itemFromRefund.quantidade) ===
                Number(item.quantidade_nova)
                  ? 'disabled'
                  : ''
              }
            >
              <img
                src={`${process.env.REACT_APP_IMAGE_URL_API}/${
                  item.img_capa ? item.img_capa : 'no-image.jpg'
                }`}
              />
              <Form.Check
                checked={isChecked}
                onChange={() => {
                  setIsChecked((prevState) => !prevState)
                  if (!isChecked) {
                    addItem({
                      ...item,
                      quantity_to_refund: quantityRefund,
                    })
                  } else {
                    removeItem(item)
                  }
                }}
                disabled={checkIsDisabled}
              />

              <div>
                <strong>#{item.codigo}</strong>
                <p>{item.descricao}</p>
                <p>Quantidade: {item.quantidade_nova}</p>
                <p>Preço Unitário: {formatPrice(itemPriceUnit)}</p>
                <p>Preço: {formatPrice(itemPriceTotal)}</p>
                <p>Quantidade Devolvida: {itemFromRefund.quantidade || '0'}</p>

                <hr />

                <p
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  Quantidade a devolver:
                  <Form.Control
                    type="number"
                    maxLength={4}
                    value={quantityRefund}
                    onChange={(e) => {
                      setIsChecked(false)
                      removeItem(item)
                      setQuantityRefund(e.target.value)
                    }}
                    style={{
                      width: 100,
                      height: 30,
                    }}
                  />
                </p>

                <p>
                  Saldo a receber: {formatPrice(itemPriceUnit * quantityRefund)}
                </p>
              </div>
            </article>
          </>
        )}
      </div>
    </>
  )
})
